import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const GLYPHS = '01<>/{}[]()=+*&%$#@!?~^|\\;:';
const randGlyph = () => GLYPHS[(Math.random() * GLYPHS.length) | 0];

// "HELLO, WORLD" — the first four cells are the ones that survive into SHELLY.
const HELLO = [
  { id: 'h', ch: 'H', keep: true },
  { id: 'e', ch: 'E', keep: true },
  { id: 'l1', ch: 'L', keep: true },
  { id: 'l2', ch: 'L', keep: true },
  { id: 'o1', ch: 'O' },
  { id: 'comma', ch: ',' },
  { id: 'gap', ch: ' ' },
  { id: 'w', ch: 'W' },
  { id: 'o2', ch: 'O' },
  { id: 'r', ch: 'R' },
  { id: 'l3', ch: 'L' },
  { id: 'd', ch: 'D' },
];

const KEPT = HELLO.filter((c) => c.keep);
const SHELLY = [
  { id: 's', ch: 'S', fresh: true },
  ...KEPT,
  { id: 'y', ch: 'Y', fresh: true },
];

const TYPE_MS = 78;

// How long to sit in each phase before advancing. Phases are chained rather than
// scheduled off a single mount timestamp so that a throttled tab (background,
// slow device) can't fire the next phase before the current one has finished —
// it just runs slower.
const HOLD = {
  boot: 620,
  typed: 300, // last character → scanner
  scan: 700,
  morph: 820,
  settle: 1100,
};
const NEXT_PHASE = { scan: 'morph', morph: 'settle', settle: 'exit' };
const TOTAL_MS =
  HOLD.boot + HELLO.length * TYPE_MS + HOLD.typed + HOLD.scan + HOLD.morph + HOLD.settle;

const BOOT_LOG = [
  '> ./portfolio --init',
  '> resolving modules ......... ok',
  '> stdout: attaching to main()',
];

// ─── Matrix-ish glyph rain ─────────────────────────────────────────────────────
const CodeRain = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const SIZE = 13;
    let raf;
    let frame = 0;
    let drops = [];
    let w = 0;
    let h = 0;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `${SIZE}px 'JetBrains Mono', monospace`;
      drops = Array.from({ length: Math.ceil(w / SIZE) }, () => Math.random() * -h);
    };

    const draw = () => {
      // translucent wash over the previous frame leaves the classic comet trail
      ctx.fillStyle = 'rgba(9,9,15,0.085)';
      ctx.fillRect(0, 0, w, h);

      if (frame % 2 === 0) {
        for (let i = 0; i < drops.length; i++) {
          const y = drops[i];
          ctx.fillStyle = 'rgba(94,234,212,0.55)';
          ctx.fillText(randGlyph(), i * SIZE, y);
          drops[i] = y > h + Math.random() * h * 0.6 ? 0 : y + SIZE;
        }
      }
      frame++;
      raf = requestAnimationFrame(draw);
    };

    setup();
    window.addEventListener('resize', setup);
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', setup);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 opacity-[0.16]" aria-hidden />;
};

// ─── A character that shuffles through junk before resolving ───────────────────
const Scramble = ({ ch, duration = 460 }) => {
  const [display, setDisplay] = useState(randGlyph);

  useEffect(() => {
    const start = performance.now();
    let raf;
    let last = 0;
    const tick = (now) => {
      if (now - start >= duration) {
        setDisplay(ch);
        return;
      }
      if (now - last > 42) {
        setDisplay(randGlyph());
        last = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ch, duration]);

  return <>{display}</>;
};

// ─── Intro ─────────────────────────────────────────────────────────────────────
const IntroScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState('boot'); // boot → type → scan → morph → settle → exit
  const [typed, setTyped] = useState(0);
  const [reduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  );

  const skip = useCallback(() => setPhase('exit'), []);

  // boot → type (or jump straight to the payoff when motion is unwelcome)
  useEffect(() => {
    if (reduced) {
      setTyped(HELLO.length);
      setPhase('settle');
      return;
    }
    const t = setTimeout(() => setPhase('type'), HOLD.boot);
    return () => clearTimeout(t);
  }, [reduced]);

  // typewriter for "HELLO, WORLD"
  useEffect(() => {
    if (phase !== 'type') return;
    const t = setInterval(() => setTyped((n) => Math.min(n + 1, HELLO.length)), TYPE_MS);
    return () => clearInterval(t);
  }, [phase]);

  // the word is fully typed → hand off to the scanner
  useEffect(() => {
    if (phase !== 'type' || typed < HELLO.length) return;
    const t = setTimeout(() => setPhase('scan'), HOLD.typed);
    return () => clearTimeout(t);
  }, [phase, typed]);

  // scan → morph → settle → exit
  useEffect(() => {
    const next = NEXT_PHASE[phase];
    if (!next) return;
    const t = setTimeout(() => setPhase(next), HOLD[phase]);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    const onKey = () => skip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);

  const morphed = phase === 'morph' || phase === 'settle' || phase === 'exit';
  const scanning = phase === 'scan';
  const cells = morphed ? SHELLY : HELLO.slice(0, typed);
  const exiting = phase === 'exit';

  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-[#09090f] flex flex-col items-center justify-center overflow-hidden cursor-pointer select-none"
      initial={{ y: 0 }}
      animate={{ y: exiting ? '-100%' : 0 }}
      transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
      onAnimationComplete={() => exiting && onComplete()}
      onClick={skip}
    >
      {!reduced && <CodeRain />}

      {/* horizontal sweep line */}
      <motion.div
        className="absolute left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(94,234,212,0.45), transparent)' }}
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* boot log */}
      <div className="absolute top-7 left-8 text-[10px] font-mono text-teal-400/30 leading-[1.7] space-y-0.5">
        {BOOT_LOG.map((line, i) => (
          <motion.div
            key={line}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.12 + i * 0.16, duration: 0.3 }}
          >
            {line}
          </motion.div>
        ))}
      </div>

      {/* ── the word ── */}
      <div className="relative z-10 flex flex-col items-center">
        <motion.div
          className="relative flex items-center font-bold text-white leading-none tracking-[-0.02em] px-4"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 'clamp(1.9rem, 7.6vw, 5.6rem)',
          }}
          animate={phase === 'settle' || exiting ? { scale: 1.06 } : { scale: 1 }}
          transition={{ type: 'spring', stiffness: 180, damping: 16 }}
        >
          {/* scanner bar sweeping the match */}
          <AnimatePresence>
            {scanning && (
              <motion.div
                className="absolute inset-y-0 left-0 w-[3px] z-20"
                style={{ background: 'rgba(94,234,212,0.9)', boxShadow: '0 0 18px 3px rgba(94,234,212,0.55)' }}
                initial={{ x: 0, opacity: 0 }}
                animate={{ x: ['0%', '1200%'], opacity: [0, 1, 1, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.62, ease: 'easeInOut' }}
              />
            )}
          </AnimatePresence>

          <AnimatePresence mode="popLayout" initial={false}>
            {cells.map((c) => {
              const doomed = scanning && !c.keep;
              const selected = scanning && c.keep;

              return (
                <motion.span
                  key={c.id}
                  layout
                  className="relative inline-block"
                  initial={c.fresh ? { opacity: 0, scale: 0.4, y: -30 } : { opacity: 0, y: 14 }}
                  animate={{
                    opacity: doomed ? 0.28 : 1,
                    scale: 1,
                    y: 0,
                    color: doomed ? '#f87171' : selected ? '#5eead4' : '#ffffff',
                    backgroundColor: selected ? 'rgba(94,234,212,0.16)' : 'rgba(94,234,212,0)',
                  }}
                  exit={{
                    opacity: 0,
                    y: 70,
                    rotateX: -95,
                    filter: 'blur(7px)',
                    transition: { duration: 0.45, ease: [0.5, 0, 0.75, 0] },
                  }}
                  transition={{
                    layout: { type: 'spring', stiffness: 260, damping: 26 },
                    default: { duration: 0.32 },
                  }}
                >
                  {c.fresh && phase === 'morph' ? <Scramble ch={c.ch} /> : c.ch}
                  {doomed && (
                    <motion.span
                      className="absolute left-0 right-0 top-1/2 h-[2px] bg-red-400/70 origin-left"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.25 }}
                    />
                  )}
                </motion.span>
              );
            })}

            {/* block cursor */}
            {!morphed && (
              <motion.span
                key="caret"
                layout
                className="inline-block bg-teal-400 ml-[0.12em]"
                style={{ width: '0.5em', height: '0.9em' }}
                animate={{ opacity: [1, 1, 0, 0] }}
                transition={{ duration: 0.9, repeat: Infinity, times: [0, 0.5, 0.5, 1] }}
              />
            )}
          </AnimatePresence>
        </motion.div>

        {/* gradient wash that sweeps SHELLY once it lands */}
        <AnimatePresence>
          {(phase === 'settle' || exiting) && (
            <motion.div
              key="glow"
              className="absolute inset-0 pointer-events-none blur-2xl -z-10"
              style={{ background: 'linear-gradient(135deg, #5eead4 0%, #818cf8 55%, #c084fc 100%)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.22 }}
              transition={{ duration: 0.7 }}
            />
          )}
        </AnimatePresence>

        {/* find / replace readout */}
        <div className="h-14 mt-7 text-[11px] font-mono text-center leading-[1.8]">
          <AnimatePresence mode="wait">
            {scanning && (
              <motion.div
                key="find"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22 }}
              >
                <span className="text-white/25">match </span>
                <span className="text-teal-400/80">/^hell/i</span>
                <span className="text-white/25"> → 1 result</span>
              </motion.div>
            )}
            {morphed && (
              <motion.div
                key="diff"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, delay: 0.15 }}
              >
                <div className="text-red-400/45">- hello, world</div>
                <motion.div
                  className="text-teal-400/75"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                >
                  + shelly
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <motion.p
          className="text-[10px] font-mono text-teal-400/45 tracking-[0.35em] uppercase"
          initial={{ opacity: 0, y: 8 }}
          animate={phase === 'settle' || exiting ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
          transition={{ duration: 0.5 }}
        >
          full-stack developer · portfolio_2025
        </motion.p>
      </div>

      <motion.span
        className="absolute bottom-7 left-8 text-[10px] font-mono text-white/12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
      >
        press any key to skip
      </motion.span>
      <motion.span
        className="absolute bottom-7 right-8 text-[10px] font-mono text-white/12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
      >
        © 2025
      </motion.span>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/[0.04]">
        <motion.div
          className="h-full bg-teal-400/40"
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: TOTAL_MS / 1000, ease: 'linear' }}
        />
      </div>
    </motion.div>
  );
};

export default IntroScreen;
